import * as React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../Authentication/components/AuthContext';
import OrgContext from '../../Authentication/components/OrgContext';
import useLogout from '../../Authentication/components/hooks/useLogout';
import { APP_CLUSTERS, V3_AREAS, areaTarget } from '../flags';
import {
    AREA_ICONS,
    IconClose,
    IconDots,
    IconMenu,
    IconSearch,
    IconUpDown,
} from './icons';
import Find from './Find';
import '../v3.css';
import { Theme, useTheme } from '../theme';
import smileMark from '../../smile_icon.png';

/*
 * App-scope chrome (brief: Chrome / app state; concept v12).
 * One nav definition (flags: V3_AREAS + APP_CLUSTERS), two renderings:
 * the desktop rail and the mobile sheet — CSS decides which shows.
 * Bridge rows navigate out to the classic surface (README rule 2).
 * The collapse control is omitted until its behavior round (concept:
 * deliberately deferred) — no dead controls.
 */

function initials(text: string): string {
    const words = String(text ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0) return '·';
    if (words.length === 1) return words[0].slice(0, 2);
    return (words[0][0] + words[1][0]).toUpperCase();
}

export function NavRows({ onNavigate }: { onNavigate?: () => void }) {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const byId = new Map(V3_AREAS.map((a) => [a.id, a]));
    return (
        <>
            {APP_CLUSTERS.map((cluster, i) => {
                const areas = cluster
                    .map((id) => byId.get(id))
                    .filter((a): a is NonNullable<typeof a> => !!a);
                if (areas.length === 0) return null;
                return (
                    <React.Fragment key={i}>
                        {i > 0 && <div className="gap" />}
                        {areas.map((area) => {
                            const Icon = AREA_ICONS[area.id];
                            const target = areaTarget(area);
                            const active =
                                pathname === area.route ||
                                pathname === target ||
                                /* a record inside the area (job page) */
                                pathname.startsWith(`${area.route}/`);
                            return (
                                <button
                                    key={area.id}
                                    className={
                                        active ? 'row active' : 'row'
                                    }
                                    title={area.label}
                                    aria-label={area.label}
                                    onClick={() => {
                                        onNavigate?.();
                                        navigate(target);
                                    }}
                                >
                                    {Icon && <Icon />}
                                    <span className="lbl">{area.label}</span>
                                </button>
                            );
                        })}
                    </React.Fragment>
                );
            })}
        </>
    );
}

function OrgMark({ compact }: { compact?: boolean }) {
    const { auth } = useContext(AuthContext);
    const { org, setOrg } = useContext(OrgContext);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const orgs: Array<{ id: number; name: string }> = auth?.orgs ?? [];

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const mark = (
        <span className="om">
            {/* the org's logo, else the app's mark (as classic does) */}
            {org ? (
                <img
                    src={(org.logo as string) || smileMark}
                    className={(org.logo as string) && org.logo !== smileMark ? undefined : 'app-mark'}
                    alt={org.name ?? 'Organization'}
                />
            ) : (
                initials('')
            )}
        </span>
    );
    if (compact) return mark;
    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                className="orgmark"
                aria-haspopup="menu"
                aria-expanded={open}
                onMouseDown={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen((v) => !v);
                    }
                }}
            >
                {mark}
                <IconUpDown />
            </button>
            {open && (
                <div className="menu" style={{ top: '100%', left: 0 }}>
                    {orgs.map((o) => (
                        <button
                            key={o.id}
                            className={
                                o.id === org?.id
                                    ? 'menu-item on'
                                    : 'menu-item'
                            }
                            onClick={() => {
                                (setOrg as (o: unknown) => void)(o);
                                setOpen(false);
                            }}
                        >
                            {o.name}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function WhoRow() {
    const { auth } = useContext(AuthContext);
    const { setOrg } = useContext(OrgContext);
    const logout = useLogout();
    const [theme, setTheme] = useTheme();
    const navigate = useNavigate();
    const roles: number[] = Array.isArray(auth?.roles) ? auth.roles : [];
    /* same audiences as the org and admin pages themselves (0 org admin, 69 global admin) */
    const orgAdmin = roles.some((r) => r === 0 || r === 69);
    const globalAdmin = roles.includes(69);
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const name = auth?.username ?? '';

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!ref.current?.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                className="who"
                title={name}
                aria-haspopup="menu"
                aria-expanded={open}
                onMouseDown={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen((v) => !v);
                    }
                }}
            >
                <span className="av">{initials(name).toUpperCase()}</span>
                <span className="n">{name}</span>
                <span className="ch">
                    <IconDots />
                </span>
            </button>
            {open && (
                <div
                    className="menu"
                    style={{ bottom: '100%', left: 8, marginBottom: 4 }}
                >
                    {(orgAdmin || globalAdmin) && (
                        <>
                            {orgAdmin && (
                                <button className="menu-item" onClick={() => { setOpen(false); navigate('/org'); }}>
                                    Organization
                                </button>
                            )}
                            {globalAdmin && (
                                <button className="menu-item" onClick={() => { setOpen(false); navigate('/admin'); }}>
                                    Admin
                                </button>
                            )}
                            <div className="msep" />
                        </>
                    )}
                    <div className="mlab">Appearance</div>
                    {(
                        [
                            ['light', 'Light'],
                            ['dark', 'Dark'],
                            ['system', 'Match system'],
                        ] as Array<[Theme, string]>
                    ).map(([value, label]) => (
                        <button
                            key={value}
                            className={theme === value ? 'menu-item on' : 'menu-item'}
                            aria-pressed={theme === value}
                            onClick={() => setTheme(value)}
                        >
                            {label}
                        </button>
                    ))}
                    <div className="msep" />
                    <button
                        className="menu-item"
                        onClick={() => {
                            (setOrg as (o: unknown) => void)(null);
                            logout();
                        }}
                    >
                        Log out
                    </button>
                </div>
            )}
        </div>
    );
}

export default function AppShell({
    children,
}: {
    children: React.ReactNode;
}) {
    const [sheetOpen, setSheetOpen] = useState(false);
    const [findOpen, setFindOpen] = useState(false);
    const { org } = useContext(OrgContext);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setFindOpen((v) => !v);
            }
            if (e.key === 'Escape') {
                setFindOpen(false);
                setSheetOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    /*
     * Portaled to <body>: classic Layout's main wrapper is `isolate`
     * (its own stacking context), so nothing inside it can paint above
     * the z-50 header. The world flip must own the viewport.
     */
    return createPortal(
        <div className="psv3">
            <aside className="rail">
                <div className="rail-top">
                    <OrgMark />
                    <span style={{ flex: 1 }} />
                    <button
                        className="ic"
                        aria-label="Find (⌘K)"
                        title="Find (⌘K)"
                        onClick={() => setFindOpen(true)}
                    >
                        <IconSearch />
                    </button>
                </div>
                <nav className="nav">
                    <NavRows />
                </nav>
                <WhoRow />
            </aside>

            <main className="content">{children}</main>

            {/* mobile rendering: the same nav, as a bar + sheet */}
            {!sheetOpen && !findOpen && (
                <div className="fmenu">
                    <button
                        className="find"
                        onClick={() => setFindOpen(true)}
                    >
                        <IconSearch />
                        Find
                    </button>
                    <span className="vsep" />
                    <button
                        className="mbtn"
                        aria-label="Menu"
                        title="Menu"
                        onClick={() => setSheetOpen(true)}
                    >
                        <IconMenu />
                    </button>
                </div>
            )}
            {sheetOpen && (
                <>
                    <div className="sheet">
                        <div className="chips">
                            <span className="chip">
                                <OrgMark compact />
                                {org?.name ?? ''}
                                <IconUpDown />
                            </span>
                        </div>
                        <nav className="nav">
                            <NavRows
                                onNavigate={() => setSheetOpen(false)}
                            />
                        </nav>
                        <WhoRow />
                    </div>
                    <div className="fmenu">
                        <button
                            className="find"
                            onClick={() => {
                                setSheetOpen(false);
                                setFindOpen(true);
                            }}
                        >
                            <IconSearch />
                            Find
                        </button>
                        <span className="vsep" />
                        <button
                            className="mbtn"
                            aria-label="Close menu"
                            title="Close menu"
                            onClick={() => setSheetOpen(false)}
                        >
                            <IconClose />
                        </button>
                    </div>
                </>
            )}
            {findOpen && <Find onClose={() => setFindOpen(false)} />}
        </div>,
        document.body,
    );
}
